/**
 * Turns an orders-settings save failure into something a person can act on.
 *
 * `companies/orders-settings` reports faults as bare code strings
 * (`res.badRequest('SALES_INVOICE_SERIES_NOT_FOUND')`), while other endpoints in
 * this app use `res.badRequest({ message })`. Axios surfaces whichever shape as
 * `err.response.data`, so the extraction below accepts both and only ever
 * yields a string — stringifying an object body is how a banner ends up reading
 * "[object Object]" and hiding the real cause.
 */

type SettingsErrorEntry = {
  /** i18n key */
  key: string;
  /** English fallback shown when the key is missing */
  def: string;
};

const SETTINGS_ERRORS: Record<string, SettingsErrorEntry> = {
  COMPANY_USER_MASTER_NOT_FOUND: {
    key: "orders_settings_err_master_required",
    def: "Only a company Master can change these settings.",
  },

  // --- Order amendment slot ---
  INVALID_ORDER_AMENDMENT_TEMPLATE_SOURCE: {
    key: "orders_settings_err_amendment_invalid_source",
    def: "Pick a valid template source for the order amendment.",
  },
  MISSING_ORDER_AMENDMENT_TEMPLATE: {
    key: "orders_settings_err_amendment_template_missing",
    def: "Choose a custom template for the order amendment, or switch it back to the system template.",
  },
  ORDER_AMENDMENT_TEMPLATE_NOT_FOUND: {
    key: "orders_settings_err_amendment_template_not_found",
    def: "The selected order amendment template no longer exists.",
  },
  ORDER_AMENDMENT_SERIES_NOT_FOUND: {
    key: "orders_settings_err_amendment_series_not_found",
    def: "The selected order amendment series no longer exists.",
  },
  ORDER_AMENDMENT_FILE_TYPE_NOT_FOUND: {
    key: "orders_settings_err_amendment_file_type_not_found",
    def: "The selected order amendment file type no longer exists.",
  },
  ORDER_AMENDMENT_CATEGORY_NOT_FOUND: {
    key: "orders_settings_err_amendment_category_not_found",
    def: "The selected order amendment category no longer exists.",
  },
  ORDER_AMENDMENT_SYSTEM_METADATA_REQUIRED: {
    key: "orders_settings_err_amendment_metadata_required",
    def: "The order amendment needs a series, a file type and a category before it can be saved.",
  },

  // --- Sales invoice slot ---
  INVALID_SALES_INVOICE_TEMPLATE_SOURCE: {
    key: "orders_settings_err_invoice_invalid_source",
    def: "Pick a valid template source for the sales invoice.",
  },
  MISSING_SALES_INVOICE_TEMPLATE: {
    key: "orders_settings_err_invoice_template_missing",
    def: "Choose a custom template for the sales invoice, or switch it back to the system template.",
  },
  SALES_INVOICE_TEMPLATE_NOT_FOUND: {
    key: "orders_settings_err_invoice_template_not_found",
    def: "The selected sales invoice template no longer exists.",
  },
  SALES_INVOICE_SERIES_NOT_FOUND: {
    key: "orders_settings_err_invoice_series_not_found",
    def: "The selected sales invoice series no longer exists.",
  },
  // The document-template `Series` above and the invoice NUMBERING register
  // (`InvoiceSeries`) are two different tables (orders-settings.js:
  // `salesInvoiceSeries` vs `salesInvoiceNumberSeries`) — this is the latter.
  SALES_INVOICE_NUMBER_SERIES_NOT_FOUND: {
    key: "orders_settings_err_invoice_number_series_not_found",
    def: "The selected invoice numbering series no longer exists.",
  },
  SALES_INVOICE_FILE_TYPE_NOT_FOUND: {
    key: "orders_settings_err_invoice_file_type_not_found",
    def: "The selected sales invoice file type no longer exists.",
  },
  SALES_INVOICE_CATEGORY_NOT_FOUND: {
    key: "orders_settings_err_invoice_category_not_found",
    def: "The selected sales invoice category no longer exists.",
  },
  SALES_INVOICE_SYSTEM_METADATA_REQUIRED: {
    key: "orders_settings_err_invoice_metadata_required",
    def: "The sales invoice needs a series, a file type and a category before it can be saved.",
  },
};

/**
 * Pull the backend's error code out of an Axios failure, whatever shape the
 * body took. Only string candidates are considered.
 */
export function extractSettingsErrorCode(err: any): string {
  const data = err?.response?.data;
  const candidates = [
    typeof data === "string" ? data : undefined,
    typeof data?.code === "string" ? data.code : undefined,
    typeof data?.message === "string" ? data.message : undefined,
    typeof err?.message === "string" ? err.message : undefined,
  ];
  const found = candidates.find((c) => c && c.trim() !== "");
  return found ? found.trim() : "";
}

/** @returns the code and a human message for it. */
export function resolveOrdersSettingsError(
  err: any,
  t: (key: string, opts?: any) => string
): { code: string; message: string } {
  const code = extractSettingsErrorCode(err);
  const known = SETTINGS_ERRORS[code];

  if (known) {
    return { code, message: t(known.key, { defaultValue: known.def }) };
  }

  return { code, message: code || t("error", { defaultValue: "Error" }) };
}

export const __TEST_ONLY__ = { SETTINGS_ERRORS };
